
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Mail, Facebook, Twitter } from 'lucide-react';
import { motion } from 'framer-motion';
import { CookiePreferences } from './CookieBanner';

const Footer = () => {
  const year = new Date().getFullYear();
  
  const footerAnimation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  };

  const footerLinks = [
    {
      title: "Produkt",
      links: [
        { text: "Features", url: "#features" },
        { text: "Preise", url: "#pricing" },
        { text: "FAQ", url: "#faq" },
        { text: "Studios in der Nähe", url: "/search" }
      ]
    },
    {
      title: "Unternehmen",
      links: [
        { text: "Über uns", url: "/about" },
        { text: "Blog", url: "/blog" },
        { text: "Karriere", url: "/careers" },
        { text: "Kontakt", url: "/contact" }
      ]
    },
    {
      title: "Rechtliches",
      links: [
        { text: "Impressum", url: "/impressum" },
        { text: "Datenschutz", url: "/datenschutz" },
        { text: "AGB", url: "/agb" },
        { render: () => <CookiePreferences /> }
      ]
    }
  ];
  
  return (
    <footer className="bg-[#131313] pt-24 border-t border-[#F8F8F8]/10">
      <motion.div 
        className="container mx-auto px-6"
        initial={footerAnimation.initial}
        animate={footerAnimation.animate}
        transition={footerAnimation.transition}
      >
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mb-16">
          {/* Logo and description */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center">
              <img alt="CHRONA" src="/lovable-uploads/bbbd313b-d6bd-4b33-af58-258cd00a4dc3.png" className="h-14" />
              <span className="text-2xl font-bold text-[#F8F8F8] ml-2">CHRONA</span>
            </Link>
            <p className="mt-4 text-[#F8F8F8]/70 max-w-md">
              Die edelste Art, Termine zu buchen. CHRONA vernetzt Studios, Salons und Dienstleister mit ihren Kunden – einfach, schnell und zuverlässig.
            </p>
            
            <div className="mt-6 flex gap-4">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noreferrer" 
                className="bg-[#F8F8F8]/5 text-[#F8F8F8]/80 hover:text-[#2C6E49] hover:bg-[#F8F8F8]/10 p-3 rounded-full transition-all duration-300" 
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noreferrer" 
                className="bg-[#F8F8F8]/5 text-[#F8F8F8]/80 hover:text-[#2C6E49] hover:bg-[#F8F8F8]/10 p-3 rounded-full transition-all duration-300" 
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noreferrer" 
                className="bg-[#F8F8F8]/5 text-[#F8F8F8]/80 hover:text-[#2C6E49] hover:bg-[#F8F8F8]/10 p-3 rounded-full transition-all duration-300" 
                aria-label="Twitter"
              >
                <Twitter size={18} />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noreferrer" 
                className="bg-[#F8F8F8]/5 text-[#F8F8F8]/80 hover:text-[#2C6E49] hover:bg-[#F8F8F8]/10 p-3 rounded-full transition-all duration-300" 
                aria-label="LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="mailto:support@chrona.it" 
                className="bg-[#F8F8F8]/5 text-[#F8F8F8]/80 hover:text-[#2C6E49] hover:bg-[#F8F8F8]/10 p-3 rounded-full transition-all duration-300" 
                aria-label="Email"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>
          
          {/* Footer links */}
          {footerLinks.map((section, idx) => (
            <div key={section.title} className="md:col-span-1">
              <h3 className="text-[#F8F8F8] font-bold mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIdx) => (
                  <li key={link.text || `link-${linkIdx}`}>
                    {link.render ? (
                      link.render()
                    ) : (
                      <a 
                        href={link.url} 
                        className="text-[#F8F8F8]/70 hover:text-[#2C6E49] transition-colors"
                      >
                        {link.text}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-[#F8F8F8]/10 py-8 flex flex-col md:flex-row items-center justify-between text-sm text-[#F8F8F8]/50">
          <p>© {year} CHRONA. Alle Rechte vorbehalten.</p>
          
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="/impressum" className="hover:text-[#2C6E49] transition-colors">Impressum</a>
            <a href="/datenschutz" className="hover:text-[#2C6E49] transition-colors">Datenschutz</a>
            <a href="/agb" className="hover:text-[#2C6E49] transition-colors">AGB</a>
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
